import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { happyHolidaysEndOfTheYearRechargeBanner, npsBanner } from '@ibk/shared-assets';
import { AppDomain } from '@ibk/shared-types';
import { Carousel } from '@ibk/shared-ui';
import { useHappyHolidaysEndOfTheYearRecharge } from '../../hooks';
import styles from './HomeSidebarBanners.module.css';
const NPS_BANNER_DISMISSED_KEY = 'ibk-nps-banner-dismissed';
const NPS_BANNER_CLICKED_KEY = 'ibk-nps-banner-clicked';
const NPS_BANNER_COMPETENCE_KEY = 'ibk-nps-banner-competence';
const CAROUSEL_INTERVAL = 7000;
const DISMISS_DELAY = 300;
const NPS_SURVEY_URLS = {
    [AppDomain.ESCRITORIO]: 'https://forms.office.com/pages/responsepage.aspx?id=ZLjMYhpqXUuOHDl97BqCWCG7WYivwSlDoac9WfP4DI9UNEc4S01BQjVNV0RIM1BEQUJPQ0k5Vk9BMSQlQCN0PWcu&route=shorturl',
    [AppDomain.EMPRESA]: 'https://forms.office.com/pages/responsepage.aspx?id=ZLjMYhpqXUuOHDl97BqCWCG7WYivwSlDoac9WfP4DI9UN1VSQUtXVUg4U1pTTjBMTFhPT0VSMDRJVyQlQCN0PWcu&route=shorturl',
};
const getCurrentCompetence = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
};
const shouldDisplayNpsBanner = (appDomain, isDismissed) => {
    if (isDismissed)
        return false;
    if (!appDomain)
        return false;
    return true;
};
const getSurveyUrlForDomain = (appDomain) => {
    if (!appDomain)
        return undefined;
    return NPS_SURVEY_URLS[appDomain];
};
export const HomeSidebarBanners = ({ onHappyHolidaysEndOfTheYearRechargeBannerClick, onNpsBannerClick, appDomain, 'data-testid': testId = 'HomeSidebarBanners', }) => {
    const { isInPeriod } = useHappyHolidaysEndOfTheYearRecharge();
    const dismissTimeoutRef = useRef(null);
    const [isBannerDismissed, setIsBannerDismissed] = useState(() => {
        try {
            const currentCompetence = getCurrentCompetence();
            const savedCompetence = localStorage.getItem(NPS_BANNER_COMPETENCE_KEY);
            if (savedCompetence !== currentCompetence) {
                return false;
            }
            return localStorage.getItem(NPS_BANNER_DISMISSED_KEY) === 'true';
        }
        catch {
            return false;
        }
    });
    useEffect(() => {
        try {
            const currentCompetence = getCurrentCompetence();
            const savedCompetence = localStorage.getItem(NPS_BANNER_COMPETENCE_KEY);
            if (savedCompetence !== currentCompetence) {
                localStorage.setItem(NPS_BANNER_CLICKED_KEY, 'false');
                localStorage.setItem(NPS_BANNER_DISMISSED_KEY, 'false');
                localStorage.setItem(NPS_BANNER_COMPETENCE_KEY, currentCompetence);
                setIsBannerDismissed(false);
            }
            else {
                if (localStorage.getItem(NPS_BANNER_CLICKED_KEY) === null) {
                    localStorage.setItem(NPS_BANNER_CLICKED_KEY, 'false');
                }
                if (localStorage.getItem(NPS_BANNER_DISMISSED_KEY) === null) {
                    localStorage.setItem(NPS_BANNER_DISMISSED_KEY, 'false');
                }
                if (localStorage.getItem(NPS_BANNER_COMPETENCE_KEY) === null) {
                    localStorage.setItem(NPS_BANNER_COMPETENCE_KEY, currentCompetence);
                }
            }
        }
        catch {
        }
        return () => {
            if (dismissTimeoutRef.current) {
                clearTimeout(dismissTimeoutRef.current);
            }
        };
    }, []);
    const showNpsBanner = useMemo(() => shouldDisplayNpsBanner(appDomain, isBannerDismissed), [appDomain, isBannerDismissed]);
    const handleNpsBannerClick = useCallback(() => {
        if (!appDomain)
            return;
        const surveyUrl = getSurveyUrlForDomain(appDomain);
        if (!surveyUrl)
            return;
        try {
            const currentCompetence = getCurrentCompetence();
            const savedCompetence = localStorage.getItem(NPS_BANNER_COMPETENCE_KEY);
            if (savedCompetence !== currentCompetence) {
                localStorage.setItem(NPS_BANNER_CLICKED_KEY, 'false');
                localStorage.setItem(NPS_BANNER_DISMISSED_KEY, 'false');
                localStorage.setItem(NPS_BANNER_COMPETENCE_KEY, currentCompetence);
                setIsBannerDismissed(false);
            }
            const hasClickedBefore = localStorage.getItem(NPS_BANNER_CLICKED_KEY) === 'true';
            if (!hasClickedBefore) {
                localStorage.setItem(NPS_BANNER_CLICKED_KEY, 'true');
                localStorage.setItem(NPS_BANNER_COMPETENCE_KEY, currentCompetence);
            }
            else {
                if (dismissTimeoutRef.current) {
                    clearTimeout(dismissTimeoutRef.current);
                }
                dismissTimeoutRef.current = setTimeout(() => {
                    localStorage.setItem(NPS_BANNER_DISMISSED_KEY, 'true');
                    localStorage.setItem(NPS_BANNER_COMPETENCE_KEY, currentCompetence);
                    setIsBannerDismissed(true);
                    dismissTimeoutRef.current = null;
                }, DISMISS_DELAY);
            }
            window.open(surveyUrl, '_blank');
            onNpsBannerClick?.();
        }
        catch {
        }
    }, [appDomain, onNpsBannerClick]);
    const banners = useMemo(() => {
        const allBanners = [];
        if (isInPeriod) {
            allBanners.push({
                id: 'banner-happy-holidays-end-of-the-year-recharge',
                image: happyHolidaysEndOfTheYearRechargeBanner,
                onClick: onHappyHolidaysEndOfTheYearRechargeBannerClick,
            });
        }
        if (showNpsBanner) {
            allBanners.push({
                id: 'banner-nps',
                image: npsBanner,
                onClick: handleNpsBannerClick,
            });
        }
        return allBanners;
    }, [isInPeriod, showNpsBanner, onHappyHolidaysEndOfTheYearRechargeBannerClick, handleNpsBannerClick]);
    return (_jsx("div", { className: styles.container, "data-testid": `view${testId}Container`, children: _jsx(Carousel, { items: banners, autoPlay: true, interval: CAROUSEL_INTERVAL, "data-testid": `carousel${testId}Carousel` }) }));
};
